import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, InputAdornment, Chip, TablePagination, Button, Snackbar, Alert
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useSchool } from '../contexts/SchoolContext';

interface StudentRecord {
  _id: string;
  name: string;
  email?: string;
  registrationId?: string;
  className?: string;
  department?: string;
  educationLevel?: string;
  isSubscribed?: boolean;
}

const SchoolStudents: React.FC = () => {
  const { students, loading, updateStudentClass } = useSchool();
  const [studentRows, setStudentRows] = useState<StudentRecord[]>([]);
  const [editingClasses, setEditingClasses] = useState<Record<string, string>>({});
  const [editingDepartments, setEditingDepartments] = useState<Record<string, string>>({});
  const [editingRegistrationIds, setEditingRegistrationIds] = useState<Record<string, string>>({});
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<'success' | 'error'>('success');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setStudentRows(students);
    setEditingClasses((prev) => {
      const next = { ...prev };
      students.forEach((student) => {
        if (!(student._id in next)) {
          next[student._id] = student.className ?? '';
        }
      });
      return next;
    });
    setEditingRegistrationIds((prev) => {
      const next = { ...prev };
      students.forEach((student) => {
        if (!(student._id in next)) {
          next[student._id] = student.registrationId ?? '';
        }
      });
      return next;
    });
    setEditingDepartments((prev) => {
      const next = { ...prev };
      students.forEach((student) => {
        if (!(student._id in next)) {
          next[student._id] = student.department ?? '';
        }
      });
      return next;
    });
  }, [students]);

  const filteredStudents = studentRows.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (student.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (student.registrationId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (student.department || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClassChange = (studentId: string, value: string) => {
    setEditingClasses((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleDepartmentChange = (studentId: string, value: string) => {
    setEditingDepartments((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleRegistrationIdChange = (studentId: string, value: string) => {
    setEditingRegistrationIds((prev) => ({ ...prev, [studentId]: value }));
  };

  const handleEditStudent = (studentId: string) => {
    setEditingStudentId(studentId);
    const student = studentRows.find((student) => student._id === studentId);
    setEditingClasses((prev) => ({
      ...prev,
      [studentId]: student?.className ?? ''
    }));
    setEditingDepartments((prev) => ({
      ...prev,
      [studentId]: student?.department ?? ''
    }));
    setEditingRegistrationIds((prev) => ({
      ...prev,
      [studentId]: student?.registrationId ?? ''
    }));
  };

  const handleCancelEdit = (studentId: string) => {
    setEditingStudentId(null);
    const student = studentRows.find((student) => student._id === studentId);
    setEditingClasses((prev) => ({
      ...prev,
      [studentId]: student?.className ?? ''
    }));
    setEditingDepartments((prev) => ({
      ...prev,
      [studentId]: student?.department ?? ''
    }));
    setEditingRegistrationIds((prev) => ({
      ...prev,
      [studentId]: student?.registrationId ?? ''
    }));
  };

  const handleSaveStudent = async (studentId: string) => {
    const updatedClass = editingClasses[studentId]?.trim() ?? '';
    const updatedDepartment = editingDepartments[studentId]?.trim() ?? '';
    const updatedRegistrationId = editingRegistrationIds[studentId]?.trim() ?? '';
    const currentStudent = studentRows.find((student) => student._id === studentId);
    if (!currentStudent) return;

    if (
      updatedClass === (currentStudent.className ?? '') &&
      updatedDepartment === (currentStudent.department ?? '') &&
      updatedRegistrationId === (currentStudent.registrationId ?? '')
    ) {
      setEditingStudentId(null);
      return;
    }

    setSavingId(studentId);
    try {
      await updateStudentClass(studentId, updatedClass, updatedRegistrationId, updatedDepartment);
      setStudentRows((prev) => prev.map((student) =>
        student._id === studentId ? { ...student, className: updatedClass, registrationId: updatedRegistrationId, department: updatedDepartment } : student
      ));
      setEditingStudentId(null);
      setNotificationMessage('Student record updated successfully.');
      setNotificationSeverity('success');
      setNotificationOpen(true);
    } catch (error) {
      console.error('Failed to update student record', error);
      setNotificationMessage('Unable to save student changes. Please try again.');
      setNotificationSeverity('error');
      setNotificationOpen(true);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Student Management
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
        Assign or update each student’s class. Click Edit to unlock the row, then Save or Cancel when you are done.
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search students by name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Registration ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Education Level</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((student) => (
                <TableRow key={student._id}>
                  <TableCell>{student.name}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={editingRegistrationIds[student._id] ?? student.registrationId ?? ''}
                      onChange={(e) => handleRegistrationIdChange(student._id, e.target.value)}
                      placeholder="Registration ID"
                      InputProps={{ readOnly: editingStudentId !== student._id }}
                      helperText={editingStudentId === student._id ? 'Save or cancel to apply changes' : 'Click Edit to enable registration ID editing'}
                    />
                  </TableCell>
                  <TableCell>{student.email}</TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={editingClasses[student._id] ?? student.className ?? ''}
                      onChange={(e) => handleClassChange(student._id, e.target.value)}
                      placeholder="Class name"
                      InputProps={{ readOnly: editingStudentId !== student._id }}
                      helperText={editingStudentId === student._id ? 'Save or cancel to apply changes' : 'Click Edit to enable class editing'}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const currentClassName = editingStudentId === student._id
                        ? editingClasses[student._id] ?? student.className ?? ''
                        : student.className ?? '';
                      const canEditDepartment = /\bSS\s*[123]\b/i.test(currentClassName);
                      return (
                        <TextField
                          fullWidth
                          size="small"
                          value={editingDepartments[student._id] ?? student.department ?? ''}
                          onChange={(e) => handleDepartmentChange(student._id, e.target.value)}
                          placeholder="Department"
                          InputProps={{ readOnly: editingStudentId !== student._id || !canEditDepartment }}
                          helperText={editingStudentId === student._id ? (
                            canEditDepartment
                              ? 'Enter department for SS1-SS3 students'
                              : 'Department can only be saved for SS1-SS3 students'
                          ) : 'Click Edit to enable changes'}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>{student.educationLevel}</TableCell>
                  <TableCell>
                    <Chip
                      label={student.isSubscribed ? 'Premium' : 'Basic'}
                      color={student.isSubscribed ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {editingStudentId === student._id ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSaveStudent(student._id)}
                          disabled={savingId === student._id}
                        >
                          {savingId === student._id ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleCancelEdit(student._id)}
                          disabled={savingId === student._id}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditStudent(student._id)}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredStudents.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {filteredStudents.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          No students found. Add students to this school or verify the enrolled student list.
        </Typography>
      )}
      <Snackbar
        open={notificationOpen}
        autoHideDuration={4000}
        onClose={() => setNotificationOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setNotificationOpen(false)} severity={notificationSeverity} sx={{ width: '100%' }}>
          {notificationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolStudents;